import { Box, Button } from "@outschool/backpack";
import { SearchUseCase } from "@outschool/gql-frontend-generated";
import { useTranslation } from "@outschool/localization";
import { onlineClassesPath } from "@outschool/routes";
import { ListingCard } from "@outschool/ui-components-website";
import { Carousel, Loading } from "@outschool/ui-legacy-component-library";
import { useIsMobile } from "@outschool/ui-utils";
import React, { useState } from "react";

import { FEATURE_FLAGS } from "../../../shared/Env";
import useTrackSearchFiltersApplied from "../../hooks/useTrackSearchFiltersApplied";
import { ActivityGridLoadingState } from "../categories/CardLoadingStates";
import CategoriesAgeFilter from "../categories/filters/CategoriesAgeFilter";
import { useListingsData } from "../categories/hooks/useListingsData";
import { useSubjectSubcategories } from "./hooks/useTutoringPageQuery";
import PageSectionTitle from "./PageSectionTitle";
import { TutoringListing } from "./TutoringListings";
import TutoringSubcategories from "./TutoringSubcategories";
import {
  PAGE_LOCATION,
  SubCategoryWithSubject,
  subject
} from "./utils/tutoringConst";

const TutoringCategoryListing = ({
  subject,
  categoryTitle
}: {
  subject: subject;
  categoryTitle: string;
}) => {
  const { searchUid, trackSearchFiltersApplied } =
    useTrackSearchFiltersApplied();
  const isMobile = useIsMobile();
  const { t } = useTranslation(
    "client\\components\\tutoring\\TutoringPage\\TutoringCategoryListing"
  );

  const subjectCategories = useSubjectSubcategories(subject);
  const subcategories =
    subjectCategories?.subcategories as SubCategoryWithSubject[];

  const [selectedSubcategorySlug, setSelectedSubcategorySlug] = useState("");
  const [selectedAge, setSelectedAge] = useState("");

  const promotedRankingsEnabled: boolean =
    FEATURE_FLAGS.PROMOTED_SEARCH_LOGGING as boolean;

  const searchFilters = {
    age: selectedAge,
    theme: subject,
    capacityMax: 1,
    order: "popular",
    q: selectedSubcategorySlug
  };

  const {
    listings,
    exactTotal,
    loading: loadingListings
  } = useListingsData({
    categorySlug: subject,
    predefinedCategoryFilters: searchFilters,
    trackSearchFiltersApplied,
    pageLocation: PAGE_LOCATION,
    useCase: SearchUseCase.Discover,
    usePromotedRanking: promotedRankingsEnabled
  });

  const loading =
    loadingListings || subjectCategories.loading || exactTotal === null;

  return (
    <Box>
      <PageSectionTitle title={categoryTitle} />

      {!!subcategories?.length && (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: "0.5rem",
            paddingBottom: "2.4rem"
          }}
        >
          <CategoriesAgeFilter
            searchFiltersAge={selectedAge}
            setSearchFiltersAge={age => {
              setSelectedAge(age);
            }}
          />

          <TutoringSubcategories
            subcategories={subcategories}
            selectedSubcategory={selectedSubcategorySlug}
            setSubcategory={slug => {
              setSelectedSubcategorySlug(slug);
            }}
          />
        </Box>
      )}
      {loading ? (
        <Box sx={{ "& > *": { padding: "0 !important" } }}>
          {!isMobile ? (
            <ActivityGridLoadingState
              cards={4}
              includeHeading={false}
              sx={{ "& > *": { padding: "0 !important" } }}
              isTutoringCLP={true}
            />
          ) : (
            <Loading sx={{ marginY: isMobile ? 100 : 250 }} delay={0} />
          )}
        </Box>
      ) : isMobile ? (
        <Box>
          {listings.slice(0, 4).map((listing, index) => (
            <Box
              key={index}
              sx={{
                paddingBottom: "1em"
              }}
            >
              <TutoringListing
                listing={listing}
                listingIndex={index}
                pageLocation={PAGE_LOCATION}
                variant={undefined}
              />
            </Box>
          ))}
        </Box>
      ) : (
        <Carousel
          sx={{
            fontSize: "small",
            "a:hover": { textDecoration: "none" },
            overflow: "auto",
            padding: "0 !important"
          }}
          innerSx={{
            justifyContent: "flex-start",
            gap: "small",
            padding: "0 !important"
          }}
          leftAlign
        >
          {listings.map((listing, index) => (
            <ListingCard
              key={listing.activity.uid}
              listing={listing}
              // @ts-ignore TS(2322): Type 'null' is not assignable to type 'number'.
              carouselRowNum={null}
              trackingLabel="tutoring_landing_page_desktop"
              trackingOrder={index}
              trackingProperties={{ searchUid }}
              openInNewTab
              passSectionUid={false}
              listingIndex={index}
              pageLocation={PAGE_LOCATION}
              isTutCLP={true}
            />
          ))}
        </Carousel>
      )}

      <Box
        sx={{ display: "flex", justifyContent: "center", marginTop: "2rem" }}
      >
        <Button
          color="primary"
          size="medium"
          variant="contained"
          href={onlineClassesPath(`${subject}-tutoring`)}
        >
          {t("See All {{subject}} Tutoring", {
            subject
          })}
        </Button>
      </Box>
    </Box>
  );
};

export default TutoringCategoryListing;
